import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Undo2, Upload, Loader2, DatabaseBackup, BedDouble, Download } from "lucide-react";

const fmtAt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function RecalcRuns({ propertyId, onChanged }) {
  const [runs, setRuns] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    api.get("/payroll-sheet/recalc-backups", { params: { property_id: propertyId } }).then((r) => setRuns(r.data)).catch(() => setRuns([]));
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);
  const undo = async (b) => {
    if (!window.confirm(`Undo "${b.label}" from ${fmtAt(b.at)}?\n\nThe ${b.days} day(s) it changed go back to exactly how they were before it ran. Anything typed on those days since then will be replaced.`)) return;
    setBusy(b.batch_id);
    try {
      const r = await api.post("/payroll-sheet/undo-recalc", { property_id: propertyId, batch_id: b.batch_id });
      toast.success(`Restored ${r.data.restored_days} day(s)`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Undo failed"); } finally { setBusy(null); }
  };
  return (
    <div>
      <h3 className="mb-2 flex items-center gap-2 text-sm font-bold"><DatabaseBackup className="h-4 w-4" /> Recalculate / Sync backups</h3>
      {runs === null ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : runs.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="rb-none">No backups yet — one is saved automatically every time Recalculate or Sync changes a day.</p>
      ) : (
        <div className="divide-y divide-border border border-border">
          {runs.map((b) => (
            <div key={b.batch_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`rb-run-${b.batch_id}`}>
              <span><b>{b.label}</b> · {fmtAt(b.at)} · {b.by} · {b.days} day{b.days > 1 ? "s" : ""}{b.range ? ` (${b.range[0]} – ${b.range[1]})` : ""}</span>
              {b.undone_at ? <span className="text-xs text-muted-foreground">Undone {fmtAt(b.undone_at)}</span> : (
                <Button size="sm" variant="outline" onClick={() => undo(b)} disabled={!!busy} data-testid={`rb-undo-${b.batch_id}`}>
                  {busy === b.batch_id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Undo2 className="mr-1 h-3 w-3" />} Undo
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RestoreRooms({ propertyId }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (dry) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry_run", dry ? "true" : "false");
    fd.append("property_id", propertyId);
    setBusy(true);
    try {
      const r = await api.post("/payroll-sheet/restore-rooms", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (dry) setPreview(r.data);
      else { toast.success(`Restored rooms for ${r.data.rooms_restored} row(s) on ${r.data.days} day(s)`); setPreview(null); setFile(null); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed"); } finally { setBusy(false); }
  };
  return (
    <div>
      <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><BedDouble className="h-4 w-4" /> Restore rooms from a database backup</h3>
      <p className="mb-2 text-xs text-muted-foreground">Upload the <code>payroll_daily</code> JSON export from Emergent support. Only room counts that are empty now are filled back in — hours and anything entered since are never changed. You'll see a preview first.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".json,.txt,application/json" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="rr-file" />
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="rr-choose-btn"><Upload className="mr-1 h-3 w-3" /> {file ? file.name : "Choose file"}</Button>
        <Button size="sm" onClick={() => run(true)} disabled={!file || busy} data-testid="rr-preview-btn">{busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} Preview</Button>
      </div>
      {preview && (
        <div className="mt-3 border border-border" data-testid="rr-preview">
          <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/40 px-3 py-2 text-sm">
            <span><b>{preview.rooms_restored}</b> room rows on <b>{preview.days}</b> day(s) will be restored ({preview.docs_in_file} days in file for this property)</span>
            <Button size="sm" onClick={() => run(false)} disabled={busy || !preview.rooms_restored} data-testid="rr-apply-btn">Restore rooms</Button>
          </div>
          {preview.changes.length > 0 && (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-foreground"><th className="px-3 py-1">Date</th><th className="px-3 py-1">Employee</th><th className="px-3 py-1">Position</th><th className="px-3 py-1 text-right">Stayover</th><th className="px-3 py-1 text-right">Checkout</th><th className="px-3 py-1 text-right">Rooms</th></tr></thead>
                <tbody>{preview.changes.map((c, i) => (
                  <tr key={i} className="border-t border-border"><td className="px-3 py-1">{c.date}</td><td className="px-3 py-1">{c.employee}</td><td className="px-3 py-1">{c.position}</td><td className="px-3 py-1 text-right">{c.stayover}</td><td className="px-3 py-1 text-right">{c.checkout}</td><td className="px-3 py-1 text-right font-semibold">{c.rooms}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RestoreFromLog({ onChanged }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (dry) => {
    setBusy(true);
    try {
      const r = await api.post("/payroll-sheet/restore-rooms-from-log", null, { params: { dry_run: dry } });
      if (dry) setPreview(r.data);
      else { toast.success(`Restored ${r.data.total_rooms} rooms on ${r.data.days} day(s)`); setPreview(null); onChanged?.(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed"); } finally { setBusy(false); }
  };
  const exportXlsx = async () => {
    setBusy(true);
    try {
      const res = await api.post("/payroll-sheet/restore-rooms-from-log", null, { params: { export: true }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Erased_Rooms_Report.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setBusy(false); }
  };
  const byProp = {};
  (preview?.changes || []).forEach((c) => { byProp[c.property] = (byProp[c.property] || 0) + c.rooms; });
  return (
    <div>
      <h3 className="mb-1 flex items-center gap-2 text-sm font-bold"><Undo2 className="h-4 w-4" /> Rooms erased by Recalculate (from Change Log)</h3>
      <p className="mb-2 text-xs text-muted-foreground">Checks every property for room totals that a Recalculate / Sync set to 0, and puts back the number recorded just before. Only rows with 0 rooms now are filled; hours are never changed. Restored rooms are totals (no stayover/checkout split).</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={exportXlsx} disabled={busy} data-testid="rl-export-btn">{busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Download className="mr-1 h-3 w-3" />} Export Excel (to match)</Button>
        <Button size="sm" variant="outline" onClick={() => run(true)} disabled={busy} data-testid="rl-preview-btn">Find erased rooms</Button>
      </div>
      <p className="mt-1 text-[0.65rem] text-muted-foreground">The Excel lists every erased entry: rooms entered before vs rooms saved now, plus a By Day sheet. Nothing is changed.</p>
      {preview && (
        <div className="mt-3 border border-border" data-testid="rl-preview">
          <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/40 px-3 py-2 text-sm">
            <span data-testid="rl-summary"><b>{preview.total_rooms}</b> rooms · {preview.rooms_restored} rows · {preview.days} day(s){Object.keys(byProp).length ? ` — ${Object.entries(byProp).map(([k, v]) => `${k}: ${v}`).join(", ")}` : ""}</span>
            <Button size="sm" onClick={() => run(false)} disabled={busy || !preview.rooms_restored} data-testid="rl-apply-btn">Restore these rooms</Button>
          </div>
          {preview.changes.length > 0 ? (
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-foreground"><th className="px-3 py-1">Property</th><th className="px-3 py-1">Date</th><th className="px-3 py-1">Housekeeper</th><th className="px-3 py-1">Position</th><th className="px-3 py-1 text-right">Rooms</th></tr></thead>
                <tbody>{preview.changes.map((c, i) => (
                  <tr key={i} className="border-t border-border"><td className="px-3 py-1">{c.property}</td><td className="px-3 py-1">{c.date}</td><td className="px-3 py-1">{c.employee}</td><td className="px-3 py-1">{c.position}</td><td className="px-3 py-1 text-right font-semibold">{c.rooms}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ) : <p className="px-3 py-2 text-xs text-muted-foreground" data-testid="rl-none">Nothing to restore — no erased rooms found.</p>}
        </div>
      )}
    </div>
  );
}

export const RecalcBackups = ({ propertyId, onChanged }) => (
  <div className="space-y-5 border border-border bg-card p-4" data-testid="recalc-backups">
    <RestoreFromLog onChanged={onChanged} />
    <RecalcRuns propertyId={propertyId} onChanged={onChanged} />
    <RestoreRooms propertyId={propertyId} />
  </div>
);
